import { useEffect, useState } from "react";
import CommonModal from "@/common/components/common/CommonModal";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { message } from "antd";
import { cn } from "@/lib/utils";
import { RM } from "./theme";

export type NewUserFormState = {
  userId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  roleId: string;
  mobile: string;
  email: string;
};

const emptyForm: NewUserFormState = {
  userId: "",
  firstName: "",
  middleName: "",
  lastName: "",
  roleId: "",
  mobile: "",
  email: "",
};

export interface NewUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleOptions: { id: number; name: string }[];
  onViewPermission: () => void;
  isEditMode?: boolean;
  initialData?: Partial<NewUserFormState> | null;
  onCreated?: () => void;
}

export function NewUserModal({
  open,
  onOpenChange,
  roleOptions,
  onViewPermission,
  isEditMode = false,
  initialData = null,
  onCreated,
}: NewUserModalProps) {
  const [form, setForm] = useState<NewUserFormState>(emptyForm);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isEditMode && initialData) {
      setForm({ ...emptyForm, ...initialData });
      return;
    }
    setForm(emptyForm);
  }, [open, isEditMode, initialData]);

  const setField = (key: keyof NewUserFormState, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCreate = () => {
    if (!form.email.trim() || !form.firstName.trim()) {
      message.warning("Please enter at least first name and email.");
      return;
    }
    message.success(isEditMode ? "User updated (demo)" : "User created (demo)");
    onCreated?.();
    setForm(emptyForm);
    onOpenChange(false);
  };

  const handleClose = () => {
    setForm(emptyForm);
    onOpenChange(false);
  };

  return (
    <CommonModal
      open={open}
      onCancel={handleClose}
      title={
        <span className={RM.modalTitle}>
          {isEditMode ? "Edit User" : "New User Create"}
        </span>
      }
      width={896}
      destroyOnClose
      maskClosable={false}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className={RM.outlineAccent}
            onClick={onViewPermission}
          >
            View Permission
          </Button>
          <Button
            type="button"
            className={RM.btnPrimary}
            onClick={handleCreate}
          >
            {isEditMode ? "Update User" : "Create User"}
          </Button>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      }
      bodyStyle={{
        maxHeight: "min(85vh, 720px)",
        overflowY: "auto",
        padding: "20px 24px",
      }}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="nu-user-id" className={RM.label}>
              User ID
            </Label>
            <Input
              id="nu-user-id"
              value={form.userId}
              onChange={e => setField("userId", e.target.value)}
              placeholder="User ID"
              className={cn(RM.input)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nu-first" className={RM.label}>
              First Name
            </Label>
            <Input
              id="nu-first"
              value={form.firstName}
              onChange={e => setField("firstName", e.target.value)}
              placeholder="First Name"
              className={cn(RM.input)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nu-middle" className={RM.label}>
              Middle Name
            </Label>
            <Input
              id="nu-middle"
              value={form.middleName}
              onChange={e => setField("middleName", e.target.value)}
              placeholder="Middle Name"
              className={cn(RM.input)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nu-last" className={RM.label}>
              Last Name
            </Label>
            <Input
              id="nu-last"
              value={form.lastName}
              onChange={e => setField("lastName", e.target.value)}
              placeholder="Last Name"
              className={cn(RM.input)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-1">
          <div className="space-y-2">
            <Label className={RM.label}>Role</Label>
            <Select
              value={form.roleId || undefined}
              onValueChange={v => setField("roleId", v)}
            >
              <SelectTrigger className={cn(RM.inputSelect, "text-foreground")}>
                <SelectValue placeholder="Designation" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map(r => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="nu-mobile" className={RM.label}>
              Mobile Number
            </Label>
            <Input
              id="nu-mobile"
              value={form.mobile}
              onChange={e => setField("mobile", e.target.value)}
              placeholder="Mobile Number"
              className={cn(RM.input)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nu-email" className={RM.label}>
              Email Address
            </Label>
            <Input
              id="nu-email"
              type="email"
              value={form.email}
              onChange={e => setField("email", e.target.value)}
              placeholder="Email Address"
              className={cn(RM.input)}
            />
          </div>
        </div>
      </div>
    </CommonModal>
  );
}
